<script setup>
import { ref, watch, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import dealReportsAPI from 'dashboard/api/dealReports';
import { usePipelinesStore } from 'dashboard/stores/pipelines';
import FunnelPipelineBlock from './FunnelPipelineBlock.vue';

const props = defineProps({
  params: { type: Object, required: true },
});

const { t } = useI18n();
const pipelinesStore = usePipelinesStore();
const isLoading = ref(true);
const hasError = ref(false);
const funnels = ref([]);

const allPipelines = computed(() => pipelinesStore.getPipelinesList);

// Um funil por pipeline: as escolhidas no filtro, ou todas se não houver filtro.
const pipelines = computed(() => {
  const selectedIds = [].concat(props.params.pipeline_id ?? []).map(String);
  if (!selectedIds.length) return allPipelines.value;
  return allPipelines.value.filter(pipeline =>
    selectedIds.includes(String(pipeline.id))
  );
});

const fetchData = async () => {
  isLoading.value = true;
  hasError.value = false;
  try {
    funnels.value = await Promise.all(
      pipelines.value.map(pipeline =>
        dealReportsAPI
          .getFunnel({ ...props.params, pipeline_id: pipeline.id })
          .then(response => ({ pipeline, data: response.data }))
      )
    );
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(() => [props.params, allPipelines.value], fetchData, {
  immediate: true,
  deep: true,
});
</script>

<template>
  <section class="border rounded-lg border-n-weak">
    <h2 class="p-3 font-medium text-n-slate-12">
      {{ t('DASHBOARD.COMERCIAL.SECTIONS.FUNNEL') }}
    </h2>

    <p v-if="hasError" class="px-3 pb-3 text-sm text-n-ruby-9">
      {{ t('DASHBOARD.COMERCIAL.LOAD_ERROR') }}
      <button type="button" class="underline" @click="fetchData">
        {{ t('DASHBOARD.COMERCIAL.RETRY') }}
      </button>
    </p>
    <p v-else-if="isLoading" class="px-3 pb-3 text-sm text-n-slate-11">
      {{ t('DASHBOARD.COMERCIAL.LOADING') }}
    </p>
    <div v-else class="px-3 pb-3">
      <p v-if="!pipelines.length" class="py-2 text-sm text-n-slate-10">
        {{ t('DASHBOARD.COMERCIAL.EMPTY_STATE') }}
      </p>
      <div v-else class="flex flex-col gap-6">
        <div v-for="item in funnels" :key="item.pipeline.id">
          <h3
            v-if="funnels.length > 1"
            class="mb-2 text-sm font-medium text-n-slate-11"
          >
            {{ item.pipeline.name }}
          </h3>
          <FunnelPipelineBlock
            :data="item.data"
            :pipeline-id="item.pipeline.id"
          />
        </div>
      </div>
    </div>
  </section>
</template>
